import { DOCUMENT } from "@angular/common";
import { ChangeDetectionStrategy, Component, effect, inject, input } from "@angular/core";
import { RouterOutlet } from "@angular/router";
import type { ApplicationFeatureType } from "@core/auth/permissions";
import type { MenuItem } from "./compose-menu-items";
import { MainContainerStore } from "./main-container.store";
import { MainContainerHeader } from "./main-container-header";
import { MainContainerSidebar } from "./main-container-sidebar";

@Component({
  selector: "layout-main-container",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet, MainContainerHeader, MainContainerSidebar],
  providers: [MainContainerStore],
  template: `
    @if (isSidebarOpen()) {
      <layout-main-container-sidebar />
    }
    <div class="flex grow flex-col">
      <layout-main-container-header>
        <ng-content select="[headerRight]" ngProjectAs="[right]" />
      </layout-main-container-header>
      <main [class]="isSidebarOpen() ? 'grow overflow-hidden md:overflow-y-auto' : 'grow overflow-y-auto'">
        <router-outlet />
      </main>
    </div>
  `,
  host: {
    '[class]': '"flex min-h-screen" + (isSidebarOpen() ? " md:ml-70" : "")',
  },
})
export class MainContainer {
  // Dependencies
  readonly #document = inject(DOCUMENT);
  readonly #mainContainerStore = inject(MainContainerStore);

  // Inputs
  readonly menuItems = input<MenuItem[]>([]);
  readonly applicationFeatures = input<Set<ApplicationFeatureType>>(new Set());
  readonly debugVariables = input<string[]>([]);

  // Computeds
  readonly isSidebarOpen = this.#mainContainerStore.isSidebarOpen;

  constructor() {
    effect(() => {
      this.#mainContainerStore.initialize(
        this.menuItems(),
        this.applicationFeatures(),
        this.debugVariables(),
      );
    });

    effect((onCleanup) => {
      if (
        typeof window === "undefined"
        || !this.isSidebarOpen()
        || !window.matchMedia("(max-width: 767px)").matches
      ) {
        return;
      }

      const previousBodyOverflow = this.#document.body.style.overflow;
      const previousDocumentOverflow = this.#document.documentElement.style.overflow;

      this.#document.body.style.overflow = "hidden";
      this.#document.documentElement.style.overflow = "hidden";

      onCleanup(() => {
        this.#document.body.style.overflow = previousBodyOverflow;
        this.#document.documentElement.style.overflow = previousDocumentOverflow;
      });
    });
  }
}
